import { DOCUMENT } from '@angular/common';
import { provideRouter, withExperimentalAutoCleanupInjectors } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, inject, provideAppInitializer, provideZoneChangeDetection, provideZonelessChangeDetection } from '@angular/core';

import { routes } from './app.routes';

/**
 * envs
 */
import { environment } from 'src/environments/environment';

/**
 * libs
*/
import Swal from 'sweetalert2';
import { provideToastr, GlobalConfig, ComponentType } from 'ngx-toastr';
import { provideFlatpickrDefaults } from 'angularx-flatpickr';
import { provideSweetAlert2 } from "@sweetalert2/ngx-sweetalert2";
import { catchError, firstValueFrom, of } from 'rxjs';
import { provideTranslateService, TranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { register as registerSwiperElements } from 'swiper/element/bundle';
import { provideDaterangepickerLocale } from 'ngx-daterangepicker-bootstrap';

/**
 * Services
 */
import { AuthenticationService, AuthenticationServiceProviders } from '@identity/authentication';
import { DataTableSourceService, LangService, LayoutService, LoadingServiceProviders, provideExport, provideUploadWatcher } from '@core/services';

/**
 * httpHandler
 */
import { SessionOrchestratorService, httpInterceptors, provideMqttCore } from '@network';


// httpLoaderFactory
// const httpLoaderFactory: (http: HttpClient) => TranslateHttpLoader = (http: HttpClient) => {
//   return new TranslateHttpLoader(http, './i18n/', '.json');
// }

// default swal config
const SwalDefaults = Swal.mixin({
  customClass: {
    popup: 'swal2-popup',
    input: 'form-control',
    denyButton: 'btn btn-warning',
    confirmButton: 'btn btn-primary',
    cancelButton: 'btn btn-outline-secondary',
  },
  buttonsStyling: false,
});

// default toast config
const ToastDefaults: Partial<GlobalConfig> = {
  maxOpened: 3,
  toastClass: "",
  closeButton: true,
  iconClasses: {
    info: 'notyf__info',
    error: 'notyf__error',
    success: 'notyf__success',
    warning: 'notyf__warning',
  },
  toastComponent: NotyfToastComponent as ComponentType<unknown>,
}

// init Swiper
registerSwiperElements();

/**
 * components
 */
import { NotyfToastComponent } from '@sharedComponents/notyf-toast/notyf-toast.component';

export const appConfig: ApplicationConfig = {
  providers: [
    LangService,
    LayoutService,
    DataTableSourceService,
    SessionOrchestratorService,
    ...LoadingServiceProviders,
    ...AuthenticationServiceProviders,
    provideExport(),
    provideUploadWatcher(),
    provideMqttCore({
      options: environment.MQTT_CONFIG,
      authProvider: {
        provide: AuthenticationService,
      },
      profiles: [],
    }),
    provideFlatpickrDefaults({
      prevArrow: "<i class='ti ti-chevron-left'></i>",
      nextArrow: "<i class='ti ti-chevron-right'></i>"
    }),
    provideDaterangepickerLocale(),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: './i18n/',
        suffix: '.json',
      }),
    }),
    provideHttpClient(
      withInterceptors(httpInterceptors)
    ),
    /**
     * Activates the stored language before the first paint.
     *
     * Nothing called `TranslateService.use()` previously — `LangService` only
     * set a fallback — which left `getCurrentLang()` undefined and
     * `onLangChange` silent for the whole first session. Every consumer that
     * resolves strings eagerly in a `computed` keyed on the language (the
     * sidebar menu, `LookupService`, the energy-source chart) therefore ran once
     * against an empty store and rendered raw translation keys, with no event to
     * bring it back.
     *
     * Blocking bootstrap on the bundle is what makes the first paint correct. A
     * failed load is swallowed rather than rethrown: a missing i18n file should
     * degrade to untranslated keys, not white-screen the app.
     */
    provideAppInitializer(() => {
      const lang = inject(LangService);
      const translate = inject(TranslateService);

      return firstValueFrom(
        translate.use(lang.lang).pipe(catchError(() => of(null))),
      );
    }),
    provideAppInitializer(() => {
      const doc = inject(DOCUMENT);
      return new Promise<void>((resolve) => {
        const script = doc.createElement('script');
        script.setAttribute('async', '')
        script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.GOOGLE_MAPS_API_KEY}`;
        script.onload = () => resolve();
        script.onerror = () => resolve();
        doc.head.appendChild(script);
      });
    }),
    provideRouter(routes,
      withExperimentalAutoCleanupInjectors()
    ),
    provideToastr(ToastDefaults),
    provideSweetAlert2({
      provideSwal: () => Promise.resolve(SwalDefaults)
    }),
    // provideZonelessChangeDetection()
    // provideZoneChangeDetection({ eventCoalescing: true }),
  ]
};
